import React, { useState, useEffect, useRef } from 'react';
import { useStyletron } from 'baseui';
import { Card } from 'baseui/card';
import { Button } from 'baseui/button';
import { NumberField } from '@base-ui-components/react/number-field';
import { useNavigate, useParams } from 'react-router-dom';
import { getCitySlug, getMatchingCityProduct, getTopAlignedScrollY } from '../utils/eventRoutes';

// ------------------------------
// 1) The subcomponent TicketCard
// ------------------------------
const TicketCard = React.memo(function TicketCard({
                                                      product,
                                                      onCheckout,
                                                      errorMessage,
                                                      isCheckoutLoading,
                                                      cardRef,
                                                  }) {
    const [css] = useStyletron();
    const [discountCode, setDiscountCode] = useState('');
    const [quantityByVariant, setQuantityByVariant] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);
    const citySlug = getCitySlug(product.city);

    const variantRowClass = css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem 0',
        borderBottom: '1px solid #ccc',
        '@media (max-width: 640px)': {
            flexDirection: 'column',
            alignItems: 'center',
        },
    });

    const variantInfoClass = css({
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minWidth: 0,
        '@media (max-width: 640px)': {
            width: '100%',
            alignItems: 'center',
            textAlign: 'center',
        },
    });

    const selectorWrapClass = css({
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
        '@media (max-width: 640px)': {
            width: '100%',
            justifyContent: 'center',
        },
    });

    const numberFieldGroupClass = css({
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: '0.5rem',
        flexShrink: 0,
    });

    const stepperButtonClass = css({
        minWidth: '44px',
        height: '44px',
        borderRadius: '999px',
        border: '1px solid #d9d9d9',
        backgroundColor: '#f2f2f2',
        fontSize: '1.75rem',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        flexShrink: 0,
    });

    const numberFieldInputClass = css({
        width: '72px',
        minWidth: '72px',
        height: '44px',
        textAlign: 'center',
        borderRadius: '14px',
        border: '1px solid #d9d9d9',
        fontSize: '1.25rem',
        flexShrink: 0,
    });

    // When "Buy Now" is clicked, send the raw quantities (without multiplication)
    const handleBuyNow = () => {
        const lineItems = [];
        if (product.variants && product.variants.length > 0) {
            for (const variant of product.variants) {
                const rawQty = quantityByVariant[variant.id] || 0;
                if (rawQty > 0) {
                    lineItems.push({ priceId: variant.id, quantity: rawQty });
                }
            }
        }
        onCheckout(product, lineItems, discountCode);
    };

    const cardContainerStyle = {
        width: 'calc(100% - 40px)',
        maxWidth: '420px',
        margin: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1.5rem',
    };

    return (
        <div
            id={citySlug ? `event-${citySlug}-${product.id}` : undefined}
            ref={cardRef}
            className="event-card-anchor"
        >
        <Card
            overrides={{
                Root: {
                    style: cardContainerStyle,
                },
            }}
        >
            <img
                src={product.imageUrl}
                alt={product.title}
                style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '4px',
                }}
            />

            {product.city && (
                <h3
                    style={{
                        marginTop: '1rem',
                        marginBottom: '0.75rem',
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        textAlign: 'center',
                        textTransform: 'uppercase',
                    }}
                >
                    {product.city}
                </h3>
            )}

            <h3
                style={{
                    marginTop: '1rem',
                    marginBottom: '0.75rem',
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    textAlign: 'center',
                }}
            >
                {product.title}
            </h3>

            <button
                type="button"
                className="event-card-toggle"
                aria-expanded={isExpanded}
                aria-controls={`event-details-${product.id}`}
                onClick={() => setIsExpanded((current) => !current)}
            >
                <span>{isExpanded ? 'Hide event details' : 'Show event details'}</span>
                <span aria-hidden="true">{isExpanded ? '−' : '+'}</span>
            </button>

            <div
                id={`event-details-${product.id}`}
                className="event-card-details"
                data-expanded={isExpanded ? 'true' : 'false'}
            >
                <MetadataBlock label="Date" value={product.date} />
                <MetadataBlock label="Time" value={product.time} />
                <MetadataBlock label="Dress Code" value={product.dress_code} />
                <MetadataBlock label="Venue" value={product.venue} />
                <MetadataBlock label="City" value={product.city} />
                <MetadataBlock label="Price" value={product.price} />

            {product.description && (
                <p
                    style={{
                        fontSize: '0.95rem',
                        margin: '1rem 0',
                        textAlign: 'center',
                    }}
                >
                    {product.description}
                </p>
            )}

            {!product.soldOut ? (
                <>
                    {product.variants && product.variants.length > 0 && (
                        <div
                            style={{
                                marginTop: '1.5rem',
                                padding: '1rem',
                                width: '100%',
                                borderRadius: '8px',
                                backgroundColor: '#f7f7f7',
                                boxSizing: 'border-box',
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 1rem',
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    textAlign: 'center',
                                }}
                            >
                                Ticket Options
                            </h4>
                            {product.lowInventory && (
                                <div
                                    style={{
                                        color: 'orange',
                                        fontSize: '0.9rem',
                                        textAlign: 'center',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    Few tickets left!
                                </div>
                            )}
                            {product.variants.map((variant) => (
                                <div
                                    key={variant.id}
                                    className={variantRowClass}
                                >
                                    <div className={variantInfoClass}>
                                        <strong>{variant.title}</strong>
                                        <div style={{ fontSize: '0.9rem', marginTop: '0.2rem' }}>
                                            {variant.price}
                                        </div>
                                    </div>

                                    <div className={selectorWrapClass}>
                                        <NumberField.Root
                                            value={quantityByVariant[variant.id] || 0}
                                            onValueChange={(val) => {
                                                const clampedValue = Math.max(val, 0);
                                                setQuantityByVariant((prev) => ({
                                                    ...prev,
                                                    [variant.id]: clampedValue,
                                                }));
                                            }}
                                        >
                                            <NumberField.Group className={numberFieldGroupClass}>
                                                <NumberField.Decrement className={stepperButtonClass}>-</NumberField.Decrement>
                                                <NumberField.Input
                                                    className={numberFieldInputClass}
                                                />
                                                <NumberField.Increment className={stepperButtonClass}>+</NumberField.Increment>
                                            </NumberField.Group>
                                        </NumberField.Root>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div
                        style={{
                            margin: '1rem 0',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <input
                            type="text"
                            placeholder="Enter discount code"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value)}
                            style={{
                                width: '80%',
                                padding: '0.75rem',
                                fontSize: '1rem',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                textAlign: 'center',
                            }}
                        />
                    </div>

                    <Button
                        disabled={isCheckoutLoading}
                        style={{ margin: '1rem auto 0', display: 'block' }}
                        onClick={handleBuyNow}
                    >
                        {isCheckoutLoading ? 'Loading checkout...' : 'Buy Now'}
                    </Button>
                </>
            ) : (
                <div
                    style={{
                        margin: '1rem 0',
                        textAlign: 'center',
                        color: 'red',
                    }}
                >
                    Sorry, tickets are all sold out.
                </div>
            )}

            {errorMessage && (
                <div
                    style={{
                        color: 'red',
                        margin: '1rem 0',
                        textAlign: 'center',
                    }}
                >
                    {errorMessage}
                </div>
            )}
            </div>
        </Card>
        </div>
    );
});

function MetadataBlock({ label, value }) {
    if (!value) return null;
    return (
        <div style={{ textAlign: 'center', margin: '0.75rem 0' }}>
            <div
                style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                }}
            >
                {label}
            </div>
            <div style={{ fontSize: '1rem' }}>{value}</div>
        </div>
    );
}

// ------------------------------------------------------------
// 2) The main Tickets component that fetches products
// ------------------------------------------------------------
export default function Tickets() {
    const [css] = useStyletron();
    const navigate = useNavigate();
    const { citySlug } = useParams();
    const [products, setProducts] = useState([]);
    const [checkoutErrors, setCheckoutErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true); // New loading state
    const [pendingCheckoutId, setPendingCheckoutId] = useState(null);
    const eventCardRefs = useRef({});

    const params = new URLSearchParams(window.location.search);
    const showGuestList = params.has('guest');

    const filteredProducts = products.map(prod => ({
        ...prod,
        variants: (prod.variants || []).filter(v =>
            v.title !== "Guest List" || showGuestList
        )
    }));

    useEffect(() => {
        async function fetchAllProducts() {
            try {
                const summaryResponse = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/storefront/products`
                );
                const summaries = await summaryResponse.json();

                const detailedProducts = await Promise.all(
                    summaries.map(async (prod) => {
                        const detailResponse = await fetch(
                            `${process.env.REACT_APP_BACKEND_URL}/storefront/product/${prod.id}`
                        );
                        const detail = await detailResponse.json();
                        return { ...prod, ...detail };
                    })
                );

                setProducts(detailedProducts);
            } catch (err) {
                console.error('Error fetching products:', err);
            } finally {
                // Set to false once the fetch has completed
                setIsLoading(false);
            }
        }
        fetchAllProducts();
    }, []);

    useEffect(() => {
        if (isLoading || !citySlug || products.length === 0) {
            return;
        }

        const matchingProduct = getMatchingCityProduct(products, citySlug);
        const targetElement = matchingProduct ? eventCardRefs.current[matchingProduct.id] : null;

        if (targetElement) {
            let isCancelled = false;
            const scrollToCardTop = () => {
                if (isCancelled) {
                    return;
                }

                const targetTop = getTopAlignedScrollY({
                    elementTop: targetElement.getBoundingClientRect().top,
                    currentScrollY: window.scrollY,
                    topOffset: 16,
                });

                window.scrollTo({ top: targetTop, behavior: 'auto' });
            };

            const eventImages = Array.from(document.querySelectorAll('#ticketList img'));
            const imageLoadPromises = eventImages.map((image) => {
                if (image.complete) {
                    return Promise.resolve();
                }

                return new Promise((resolve) => {
                    image.addEventListener('load', resolve, { once: true });
                    image.addEventListener('error', resolve, { once: true });
                });
            });

            const correctionTimers = [500, 1500, 3000].map((delay) =>
                window.setTimeout(scrollToCardTop, delay)
            );

            window.requestAnimationFrame(scrollToCardTop);
            Promise.all(imageLoadPromises).then(() => {
                window.requestAnimationFrame(scrollToCardTop);
            });

            return () => {
                isCancelled = true;
                correctionTimers.forEach(window.clearTimeout);
            };
        }
    }, [citySlug, isLoading, products]);

    const handleCheckout = async (product, lineItems, discountCode) => {
        setCheckoutErrors((prev) => ({ ...prev, [product.id]: '' }));

        if (lineItems.length === 0) {
            showTemporaryError(product.id, 'Please select at least one ticket.');
            return;
        }

        const effectiveTotal = product.variants.reduce((sum, variant) => {
            const rawQty = lineItems.find((item) => item.priceId === variant.id)
                ?.quantity || 0;
            let multiplier = 1;
            const match = variant.title.match(/group of (\d+)/i);
            if (match && match[1]) {
                multiplier = parseInt(match[1], 10);
            }
            return sum + rawQty * multiplier;
        }, 0);

        if (effectiveTotal > product.inventoryCount) {
            showTemporaryError(
                product.id,
                `There are only ${product.inventoryCount} tickets left.`
            );
            return;
        }

        const payload = { lineItems, discountCode, productId: product.id };
        console.log('Sending checkout payload:', JSON.stringify(payload, null, 2));

        try {
            setPendingCheckoutId(product.id);
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/storefront/checkout`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                showTemporaryError(product.id, data.error || 'Checkout error. Please try again later.');
                return;
            }

            const selectedItems = lineItems.map((item) => {
                const variant = product.variants.find((variantOption) => variantOption.id === item.priceId);
                return {
                    id: item.priceId,
                    title: variant?.title || 'Ticket',
                    price: variant?.price || '',
                    quantity: item.quantity,
                };
            });

            const checkoutState = {
                checkoutId: data.checkoutId,
                requiresPayment: data.requiresPayment,
                clientSecret: data.clientSecret,
                paymentIntentId: data.paymentIntentId,
                amount: data.amount,
                currency: data.currency,
                inventoryReservationExpiresAt: data.inventoryReservationExpiresAt,
                customerEmail: data.customerEmail,
                product: {
                    id: product.id,
                    title: product.title,
                    imageUrl: product.imageUrl,
                    date: product.date,
                    time: product.time,
                    venue: product.venue,
                    city: product.city,
                },
                lineItems: selectedItems,
            };

            sessionStorage.setItem(
                `apt300-checkout:${data.checkoutId}`,
                JSON.stringify(checkoutState)
            );

            navigate(`/checkout?checkout_id=${encodeURIComponent(data.checkoutId)}`);
        } catch (error) {
            console.error('Error during checkout:', error);
            showTemporaryError(product.id, 'Checkout failed. Please try again.');
        } finally {
            setPendingCheckoutId(null);
        }
    };

    function showTemporaryError(productId, message) {
        setCheckoutErrors((prev) => ({ ...prev, [productId]: message }));
        setTimeout(() => {
            setCheckoutErrors((prev) => ({ ...prev, [productId]: '' }));
        }, 5000);
    }

    return (
        <div
            id="ticketList"
            className={css({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                background: 'var(--apt300-orange-gradient)',
                backgroundAttachment: 'scroll',
                minHeight: '100vh',
                marginBottom: '0',
                padding: '0 0.5rem',
                overflowX: 'hidden',
            })}
        >
            <h1
                className={css({
                    fontFamily: 'Impact, sans-serif',
                    fontWeight: 'bold',
                    fontSize: '3rem',
                    margin: '2rem 0 0.5rem',
                    color: '#fff',
                    textAlign: 'center',
                    lineHeight: 1,
                })}
            >
                CATCH US AT OUR NEXT EVENT
            </h1>
            <h2
                className={css({
                    fontFamily: 'Impact, sans-serif',
                    fontWeight: 'normal',
                    fontSize: '1.5rem',
                    margin: '0 0 2rem',
                    color: '#fff',
                    textAlign: 'center',
                })}
            >
                APT 300 Upcoming Events
            </h2>

            {isLoading ? (
                <div
                    id="loading"
                    className={css({
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '50vh',
                        width: '100%',
                    })}
                >
                    <img
                        src="/apt300-loading.GIF"
                        alt="Loading..."
                        style={{ maxWidth: '100px', width: '100%' }}
                    />
                </div>
            ) : (
                <div
                    className={css({
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        width: '100%',
                        maxWidth: '1200px',
                    })}
                >
                    {filteredProducts.map((product) => (
                        <TicketCard
                            key={product.id}
                            product={product}
                            onCheckout={handleCheckout}
                            errorMessage={checkoutErrors[product.id]}
                            isCheckoutLoading={pendingCheckoutId === product.id}
                            cardRef={(element) => {
                                if (element) {
                                    eventCardRefs.current[product.id] = element;
                                } else {
                                    delete eventCardRefs.current[product.id];
                                }
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
